<div>
  <div class="container-fluid">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col-md-6">
        <h1 class="h2 mb-1 fw-bold">Safety Tests</h1>
        <p class="text-muted">Manage knowledge tests and certifications</p>
      </div>
      <div class="col-md-6 text-md-end">
        <button type="button" class="btn btn-outline-secondary me-2" disabled title="Coming soon">
          <i class="bi bi-bar-chart me-2"></i>
          Analytics
        </button>
        <button type="button" class="btn btn-outline-secondary me-2" (click)="openCategoryModal()">
          <i class="bi bi-gear me-2"></i>
          Categories
        </button>
        <button type="button" class="btn btn-primary" (click)="createTest()">
          <i class="bi bi-plus-circle me-2"></i>
          Add Test
        </button>
      </div>
    </div>

    <!-- Filters & Search -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <!-- Search -->
          <div class="col-md-4">
            <label class="form-label small text-body-secondary">Search</label>
            <div class="input-group">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                class="form-control"
                placeholder="Search by title, short name..."
                [(ngModel)]="searchQuery"
                (keyup.enter)="onSearch()"
              />
              <button class="btn btn-outline-secondary" type="button" (click)="onSearch()">
                Search
              </button>
            </div>
          </div>

          <!-- Status Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Status</label>
            <select class="form-select" [(ngModel)]="filterStatus" (change)="onFilterChange()">
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="retired">Retired</option>
            </select>
          </div>

          <!-- Category Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Category</label>
            <select class="form-select" [(ngModel)]="filterCategory" (change)="onFilterChange()">
              <option value="all">All Categories</option>
              <option *ngFor="let cat of categories" [value]="cat.id">{{ cat.shortName || cat.name }}</option>
            </select>
          </div>

          <!-- Sort -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Sort By</label>
            <select class="form-select" [(ngModel)]="sortBy" (change)="onSortChange()">
              <option value="createdAt">Date Created</option>
              <option value="title">Title</option>
              <option value="version">Version</option>
            </select>
          </div>

          <!-- Sort Order -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Order</label>
            <select class="form-select" [(ngModel)]="sortOrder" (change)="onSortChange()">
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>

          @if (showClearFilters()) {
            <div class="row mt-3">
              <div class="col-12">
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="resetFilters()">
                  <i class="bi bi-x-circle me-1"></i>
                  Clear Filters
                </button>
              </div>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Tests Table -->
    <div *ngIf="!loading" class="card overflow-hidden">
      <div class="table-responsive">
        <table class="table data-table table-hover mb-0">
          <thead>
            <tr>
              <th>Test</th>
              <th>Category</th>
              <th>Status</th>
              <th class="text-center">Questions</th>
              <th class="text-center">Version</th>
              <th>Created</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngIf="tests.length === 0">
              <td colspan="7" class="text-center text-muted py-4">
                No tests found
              </td>
            </tr>
            <tr *ngFor="let test of tests" class="cursor-pointer" (click)="viewTest(test.id)">
              <td>
                <div class="d-flex align-items-center gap-3">
                  <div class="entity-avatar">
                    <i [class]="'bi ' + 'bi-clipboard-check'"></i>
                  </div>
                  <div>
                    <div class="fw-medium">{{ test.title }}</div>
                    <small class="text-muted" *ngIf="test.shortName">{{ test.shortName }}</small>
                  </div>
                </div>
              </td>
              <td>
                <span>{{ getCategoryName(test.categoryId) }}</span>
              </td>
              <td>
                <span class="badge" [ngClass]="getStatusClass(test.status)">
                  <i class="bi"
                    [class.bi-pencil]="test.status === 'draft'"
                    [class.bi-check-circle]="test.status === 'active'"
                    [class.bi-archive]="test.status === 'retired'"
                  ></i>
                  {{ getStatusLabel(test.status) }}
                </span>
              </td>
              <td class="text-center">
                <span class="badge bg-light text-dark">{{ test.questionCount || 0 }}</span>
              </td>
              <td class="text-center">
                <span class="text-muted">v{{ test.version }}</span>
              </td>
              <td>
                <small class="text-muted">{{ formatDate(test.createdAt) }}</small>
              </td>
              <td class="text-end">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary me-1"
                  (click)="viewTest(test.id); $event.stopPropagation()"
                  title="View Details"
                >
                  <i class="bi bi-eye"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  (click)="editTest(test.id); $event.stopPropagation()"
                  title="Edit"
                >
                  <i class="bi bi-pencil"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="card-footer" *ngIf="totalPages > 1">
        <div class="d-flex justify-content-between align-items-center">
          <div class="text-muted small">
            Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalTests) }} of {{ totalTests }} tests
          </div>
          <nav>
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" [class.disabled]="currentPage === 1">
                <a class="page-link" (click)="goToPage(currentPage - 1)">
                  <i class="bi bi-chevron-left"></i>
                </a>
              </li>
              <li
                *ngFor="let page of paginationRange"
                class="page-item"
                [class.active]="page === currentPage"
              >
                <a class="page-link" (click)="goToPage(page)">{{ page }}</a>
              </li>
              <li class="page-item" [class.disabled]="currentPage === totalPages">
                <a class="page-link" (click)="goToPage(currentPage + 1)">
                  <i class="bi bi-chevron-right"></i>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Categories Modal -->
<div class="modal fade" id="categoryModal" tabindex="-1">
  <div class="modal-dialog modal-lg">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-gear me-2"></i>Manage Categories
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal"></button>
      </div>
      <div class="modal-body">
        <!-- Category List -->
        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0">Categories</h6>
          </div>
          <div class="list-group" *ngIf="categories.length > 0">
            <div *ngFor="let cat of categories" class="list-group-item d-flex justify-content-between align-items-center">
              <div>
                <div class="fw-medium">{{ cat.name }}</div>
                <small class="text-muted" *ngIf="cat.shortName">{{ cat.shortName }}</small>
                <small class="text-muted d-block" *ngIf="cat.description">{{ cat.description }}</small>
              </div>
              <div class="btn-group btn-group-sm">
                <button type="button" class="btn btn-outline-secondary" (click)="editCategory(cat)" title="Edit">
                  <i class="bi bi-pencil"></i>
                </button>
                <button type="button" class="btn btn-outline-danger" (click)="deleteCategory(cat)" [disabled]="deletingCategory" title="Delete">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>
          <div *ngIf="categories.length === 0" class="text-muted text-center py-3">
            No categories yet. Add one below.
          </div>
        </div>

        <!-- Add/Edit Category Form -->
        <div class="card">
          <div class="card-header">
            <h6 class="mb-0">{{ editingCategory ? 'Edit Category' : 'Add New Category' }}</h6>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label small text-body-secondary">Name <span class="text-danger">*</span></label>
                <input type="text" class="form-control" [(ngModel)]="categoryForm.name" placeholder="e.g., Core Safety">
              </div>
              <div class="col-md-6">
                <label class="form-label small text-body-secondary">Short Name</label>
                <input type="text" class="form-control" [(ngModel)]="categoryForm.shortName" placeholder="e.g., CORE">
              </div>
              <div class="col-12">
                <label class="form-label small text-body-secondary">Description</label>
                <textarea class="form-control" [(ngModel)]="categoryForm.description" rows="2" placeholder="Brief description..."></textarea>
              </div>
            </div>
            <div class="mt-3 d-flex gap-2">
              <button type="button" class="btn btn-primary" (click)="saveCategory()" [disabled]="!categoryForm.name.trim() || savingCategory">
                <span *ngIf="savingCategory" class="spinner-border spinner-border-sm me-1"></span>
                {{ editingCategory ? 'Update' : 'Add' }} Category
              </button>
              <button *ngIf="editingCategory" type="button" class="btn btn-outline-secondary" (click)="cancelEditCategory()">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
      </div>
    </div>
  </div>
</div>
